import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {
    VisionProviderDiagnostics,
    VisionProviderSubscription,
    VisionSubscriptionConsistencyReport,
} from '../../models/vision-provider-management';
import {
    VisionProviderManagementViewService,
    VisionProviderSubscriptionApiService,
} from '../../services/vision-provider-management';
import {
    EAGLE_EYE_EVENT_TYPE_OPTIONS,
    EagleEyeEventTypeOption,
    formatEventTypeList,
} from '../../services/vte/eagle-eye-event-types';
import {applyPaginatedResponse, createVteListState, VteListState,} from '../../services/vte/vte-list.utils';
import {VtePaginationComponent} from '../../shared/vte';

@Component({
    selector: 'app-provider-event-subscriptions',
    standalone: true,
    imports: [CommonModule, FormsModule, VtePaginationComponent],
    templateUrl: './provider-event-subscriptions.component.html',
    styleUrl: './provider-event-subscriptions.component.css',
})
export class ProviderEventSubscriptionsComponent implements OnInit {
    providerId = '';
    subscriptions: VisionProviderSubscription[] = [];
    diagnostics: VisionProviderDiagnostics | null = null;
    consistencyReport: VisionSubscriptionConsistencyReport | null = null;
    consistencyLoadFailed = false;
    readonly listState: VteListState = createVteListState();
    readonly eventTypeOptions: EagleEyeEventTypeOption[] = EAGLE_EYE_EVENT_TYPE_OPTIONS;
    showReplaceForm = false;
    saving = false;
    comparing = false;
    selectedSubscription: VisionProviderSubscription | null = null;

    cameraIdsInput = '';
    selectedEventType = EAGLE_EYE_EVENT_TYPE_OPTIONS[0]?.eventTypeId || '';

    readonly mismatchWarningCopy =
        'KYRA and EEN subscription records do not match. Review synchronization before relying on provider event delivery.';
    readonly unverifiedWarningCopy =
        'Subscription synchronization status could not be verified.';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private api: VisionProviderSubscriptionApiService,
        private viewService: VisionProviderManagementViewService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.route.parent?.paramMap.subscribe((params) => {
            this.providerId = params.get('providerId') || '';
            this.loadPage();
        });
    }

    formatEventTypes = formatEventTypeList;

    get environmentResolved(): boolean {
        return this.viewService.isEnvironmentResolved(this.diagnostics);
    }

    get environmentBannerTitle(): string {
        if (!this.environmentResolved) {
            return 'Current environment: Unresolved';
        }
        return `Current environment: ${this.viewService.formatEnvironmentLabel(this.diagnostics?.environment)}`;
    }

    get environmentBannerDetail(): string {
        if (!this.environmentResolved) {
            return this.diagnostics?.environment_unresolved_reason
                || 'Remote subscription changes are blocked until DJANGO_ENV and KYRA_PUBLIC_API_BASE_URL agree.';
        }
        return (
            'KYRA status ACTIVE does not prove the Eagle Eye remote subscription still exists. ' +
            'This page compares EEN automatically on entry; use Refresh to retry.'
        );
    }

    get lastSuccessfulSyncLabel(): string {
        return this.viewService.formatTimestamp(this.viewService.getLastSynchronizedAt(this.providerId));
    }

    get comparisonError(): string | null {
        return this.viewService.getLastComparisonError(this.providerId);
    }

    /** Confirmed KYRA↔EEN drift from the authoritative consistency API. */
    get showMismatchWarning(): boolean {
        return this.consistencyReport?.mismatch === true;
    }

    /**
     * Unverified remote state (or failed consistency fetch).
     * Never shown alongside the mismatch banner; mismatch wins when both apply.
     */
    get showUnverifiedWarning(): boolean {
        if (this.showMismatchWarning) {
            return false;
        }
        if (this.consistencyLoadFailed) {
            return true;
        }
        return this.consistencyReport?.unverified === true;
    }

    loadPage(): void {
        this.loadDiagnostics();
        this.loadSubscriptions();
        this.loadConsistencyReport();
        this.refreshRemoteComparison(false);
    }

    loadDiagnostics(): void {
        if (!this.providerId) {
            return;
        }
        this.api.getDiagnostics(this.providerId).subscribe({
            next: (diagnostics) => {
                this.diagnostics = diagnostics;
            },
            error: (error) => {
                this.diagnostics = null;
                this.toastr.error(
                    this.viewService.formatVisionSubscriptionError(error, 'Unable to load environment diagnostics.'),
                );
            },
        });
    }

    loadConsistencyReport(): void {
        if (!this.providerId) {
            return;
        }
        this.api.getConsistencyReport(this.providerId).subscribe({
            next: (report) => {
                this.consistencyReport = report;
                this.consistencyLoadFailed = false;
            },
            error: () => {
                this.consistencyReport = null;
                this.consistencyLoadFailed = true;
            },
        });
    }

    loadSubscriptions(): void {
        if (!this.providerId) {
            return;
        }
        this.listState.loading = true;
        this.api.listSubscriptions(
            this.providerId,
            false,
            this.listState.currentPage,
            this.listState.pageSize,
            true,
        ).subscribe({
            next: (response) => {
                this.subscriptions = response.results || [];
                applyPaginatedResponse(this.listState, response);
                this.listState.loading = false;
            },
            error: (error) => {
                this.subscriptions = [];
                this.listState.loading = false;
                this.toastr.error(
                    this.viewService.formatVisionSubscriptionError(error, 'Unable to load subscriptions.'),
                );
            },
        });
    }

    refreshSubscriptions(): void {
        this.refreshRemoteComparison(true);
    }

    reviewSynchronization(): void {
        if (!this.providerId) {
            return;
        }
        void this.router.navigate([
            '/vision-configuration/provider-management/providers',
            this.providerId,
            'diagnostics',
        ]);
    }

    private refreshRemoteComparison(manual: boolean): void {
        if (!this.providerId) {
            return;
        }
        this.comparing = true;
        this.viewService.refreshRemoteComparison(this.providerId).subscribe({
            next: (report) => {
                this.comparing = false;
                this.loadConsistencyReport();
                if (this.viewService.isComparisonAvailable(report)) {
                    if (manual) {
                        this.toastr.success('Subscription state refreshed.');
                        this.loadSubscriptions();
                    }
                    return;
                }
                this.toastr.warning(this.viewService.formatComparisonUnavailableMessage(report));
            },
            error: (error) => {
                this.comparing = false;
                this.loadConsistencyReport();
                this.toastr.error(
                    this.viewService.formatVisionSubscriptionError(error, 'Unable to refresh subscriptions.'),
                );
            },
        });
    }

    openReplaceForm(subscription: VisionProviderSubscription): void {
        if (!this.canReplace(subscription)) {
            this.toastr.warning(this.mutationReason(subscription) || 'Replace is not available for this subscription.');
            return;
        }
        this.selectedSubscription = subscription;
        this.showReplaceForm = true;
        this.cameraIdsInput = (subscription.camera_ids || []).join(', ');
        this.selectedEventType = subscription.event_types?.[0]
            || EAGLE_EYE_EVENT_TYPE_OPTIONS[0]?.eventTypeId
            || '';
    }

    closeDialogs(): void {
        this.showReplaceForm = false;
        this.selectedSubscription = null;
        this.saving = false;
    }

    submitReplace(): void {
        if (!this.selectedSubscription) {
            return;
        }
        const cameraIds = this.viewService.parseListInput(this.cameraIdsInput);
        if (!cameraIds.length) {
            this.toastr.warning('At least one camera id is required.');
            return;
        }
        if (!this.selectedEventType) {
            this.toastr.warning('Select an event type.');
            return;
        }

        this.saving = true;
        this.api.replaceSubscription(this.selectedSubscription.subscription_id, {
            provider_id: this.providerId,
            camera_ids: cameraIds,
            event_types: [this.selectedEventType],
        }).subscribe({
            next: () => {
                this.toastr.success('Subscription replaced.');
                this.closeDialogs();
                this.viewService.invalidateComparison(this.providerId);
                this.loadSubscriptions();
                this.loadConsistencyReport();
            },
            error: (error) => {
                this.saving = false;
                this.toastr.error(
                    this.viewService.formatVisionSubscriptionError(error, 'Unable to replace subscription.'),
                );
            },
        });
    }

    onPageChange(page: number): void {
        this.listState.currentPage = page;
        this.loadSubscriptions();
    }

    onPageSizeChange(pageSize: number): void {
        this.listState.pageSize = pageSize;
        this.listState.currentPage = 1;
        this.loadSubscriptions();
    }

    readonly kyraStatusHelp =
        'Status of this subscription in KYRA’s database. It does not prove the Eagle Eye remote exists.';

    kyraStatusLabel(subscription: VisionProviderSubscription): string {
        return this.viewService.formatKyraStatusLabel(subscription.status);
    }

    environmentLabel(subscription: VisionProviderSubscription): string {
        return this.viewService.formatEnvironmentLabel(subscription.environment);
    }

    environmentClass(subscription: VisionProviderSubscription): string {
        return this.viewService.environmentBadgeClass(subscription.environment);
    }

    organizationLabel(subscription: VisionProviderSubscription): string {
        return this.viewService.formatOrganizationLabel(
            subscription.organization_id,
            subscription.organization_name,
        );
    }

    remoteLabel(subscription: VisionProviderSubscription): string {
        return this.viewService.formatRemoteComparisonLabel(
            this.viewService.remoteComparison(
                subscription.subscription_id,
                this.viewService.getLastSyncReport(this.providerId),
            ),
        );
    }

    remoteHelp(subscription: VisionProviderSubscription): string {
        return this.viewService.formatRemoteComparisonHelp(
            this.viewService.remoteComparison(
                subscription.subscription_id,
                this.viewService.getLastSyncReport(this.providerId),
            ),
        );
    }

    remoteClass(subscription: VisionProviderSubscription): string {
        const comparison = this.viewService.remoteComparison(
            subscription.subscription_id,
            this.viewService.getLastSyncReport(this.providerId),
        );
        return `subscription-status-badge subscription-status-badge--${comparison.replace('_', '-')}`;
    }

    canReplace(subscription: VisionProviderSubscription): boolean {
        return this.viewService.canReplaceSubscription(subscription, this.diagnostics);
    }

    showLifecycleActions(subscription: VisionProviderSubscription): boolean {
        return !this.viewService.isDeletedSubscription(subscription);
    }

    mutationReason(subscription: VisionProviderSubscription): string | null {
        return this.viewService.mutationDisabledReason(subscription, this.diagnostics);
    }

    statusClass(status: string): string {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'active') {
            return 'subscription-status-badge subscription-status-badge--active';
        }
        if (normalized === 'deleted' || normalized === 'disabled') {
            return `subscription-status-badge subscription-status-badge--${normalized}`;
        }
        return 'subscription-status-badge';
    }

    formatTimestamp(value: string | null): string {
        return this.viewService.formatTimestamp(value);
    }
}
